import { useEffect, useRef, useState } from "react";

import { toast } from "sonner";
import { Loader2, LifeBuoy, Paperclip } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { pageBeforeSupport } from "@/lib/lastPage";
import { PendingFileChips } from "@/components/Attachments";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const URGENCIES = [
  {
    key: "question",
    label: "Just a question",
    hint: "Not blocking anything",
  },
  {
    key: "problem",
    label: "Something's wrong",
    hint: "A workaround exists",
  },
  {
    key: "blocked",
    label: "Can't work",
    hint: "Nothing else can happen until it's fixed",
  },
];

const MAX_FILES = 10;

export default function SupportDialog({ open, onOpenChange, onSent }) {
  const [message, setMessage] = useState("");
  const [page, setPage] = useState("");
  const [urgency, setUrgency] = useState("question");
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);
  useEffect(() => {
    if (open) {
      setMessage("");
      setUrgency("question");
      setFiles([]);
      // Whatever they were looking at before coming here is the likely answer.
      setPage(pageBeforeSupport() || "");
    }
  }, [open]);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setFiles((cur) => {
      const merged = [...cur, ...incoming];
      if (merged.length > MAX_FILES) {
        toast.error(`Attach at most ${MAX_FILES} files at once`);
        return merged.slice(0, MAX_FILES);
      }
      return merged;
    });
  };
  const removeFile = (idx) => setFiles((cur) => cur.filter((_, i) => i !== idx));

  // Only pages this business actually has.
  const pages = [
    { path: "/dashboard", label: "Overview" },
    { path: "/messages", label: "Messages" },
    { path: "/settings", label: "Settings" },
    { path: "/team", label: "Team" },
  ].filter(Boolean);

  const submit = async () => {
    if (!message.trim() && files.length === 0) {
      toast.error("Tell us what's happening first");
      return;
    }
    setBusy(true);
    try {
      // The page they're on is sent automatically — it's the question we'd
      // otherwise have to ask them.
      const form = new FormData();
      form.append("message", message);
      form.append("urgency", urgency);
      form.append("page", page === "none" ? "" : page);
      files.forEach((f) => form.append("files", f));
      const { data } = await api.post("/support/tickets", form);
      toast.success("Sent. We'll get back to you.");
      setMessage("");
      setFiles([]);
      onSent?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Get help</DialogTitle>
          <DialogDescription>
            Describe what's happening and we'll pick it up. The reply will show
            on your Support page.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label htmlFor="msg">What's happening?</Label>
            <textarea
              id="msg"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              maxLength={4000}
              placeholder="I tried to record a sale and it said…"
              className="w-full rounded-lg border border-line bg-card px-3 py-2.5 text-[13.5px] text-ink focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
            />
            <p className="text-[11.5px] text-muted2">
              If something went wrong, what you were doing just before helps most.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>Which page is it about?</Label>
            <Select value={page || "none"} onValueChange={setPage}>
              <SelectTrigger>
                <SelectValue placeholder="Choose one" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not about a particular page</SelectItem>
                {pages.map((p) => (
                  <SelectItem key={p.path} value={p.path}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11.5px] text-muted2">
              Filled in from where you just were — change it if that's not right.
            </p>
          </div>

          <div className="space-y-2">
            <Label>How urgent is it?</Label>
            <div className="grid gap-2">
              {URGENCIES.map((u) => (
                <button
                  key={u.key}
                  type="button"
                  onClick={() => setUrgency(u.key)}
                  className={cn(
                    "text-left rounded-xl border px-3.5 py-2.5 transition-colors",
                    urgency === u.key
                      ? "border-brand bg-brand-light"
                      : "border-line hover:bg-brand-light/40"
                  )}
                >
                  <div
                    className={cn(
                      "text-[13.5px] font-medium",
                      urgency === u.key ? "text-brand" : "text-ink"
                    )}
                  >
                    {u.label}
                  </div>
                  <div className="text-[12px] text-muted2 mt-0.5">{u.hint}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Attach a screenshot or file</Label>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = "";
              }}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              className="h-8"
            >
              <Paperclip className="w-3.5 h-3.5 mr-1.5" /> Add file
            </Button>
            <PendingFileChips files={files} onRemove={removeFile} />
          </div>

        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button
            onClick={submit}
            disabled={busy || (!message.trim() && files.length === 0)}
            className="bg-brand hover:bg-brand-hover"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <LifeBuoy className="w-4 h-4 mr-2" /> Send
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
